import { useState, useRef } from 'react'
import { Stack, TextField, Checkbox } from '@fluentui/react'
import { SendRegular, AttachRegular } from '@fluentui/react-icons'

import Send from '../../assets/Send.svg'
import { uploadCVBackground } from '../../api'

import styles from './QuestionInput.module.css'

interface Props {
  onSend: (question: string, id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [uploadMessage, setUploadMessage] = useState<string>('')
  const [updateExisting, setUpdateExisting] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    if (conversationId) {
      onSend(question, conversationId)
    } else {
      onSend(question)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Snapshot FileList into a plain array before any async work —
    // the live FileList reference can become stale after an await.
    const fileArray = Array.from(files)

    // Clear file input immediately so the reference isn't held
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    // Filter to valid file types
    const validTypes = ['.pdf', '.docx', '.doc']
    const validFiles = fileArray.filter(file => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
      if (!validTypes.includes(ext)) {
        setUploadMessage(`Skipped ${file.name}: unsupported file type`)
        return false
      }
      return true
    })

    if (validFiles.length === 0) return

    // Show progress panel immediately with all filenames before uploads start
    window.dispatchEvent(
      new CustomEvent('cv-uploads-started', {
        detail: { filenames: validFiles.map(f => f.name) }
      })
    )

    // Upload all files in parallel
    const results = await Promise.allSettled(
      validFiles.map(file => uploadCVBackground(file, updateExisting))
    )

    const startedCount = results.filter(r => r.status === 'fulfilled').length
    const failedFiles = validFiles.filter((_, i) => results[i].status === 'rejected')

    if (failedFiles.length > 0) {
      setUploadMessage(`Failed to upload: ${failedFiles.map(f => f.name).join(', ')}`)
    } else if (startedCount > 0) {
      const msg = startedCount === 1
        ? `Upload started for ${validFiles[0].name}`
        : `${startedCount} uploads started`
      setUploadMessage(msg)
    }

    // Clear message after 5 seconds
    setTimeout(() => setUploadMessage(''), 5000)
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <Stack className={styles.questionInputContainer}>
      {uploadMessage && (
        <div className={styles.uploadMessage}>
          {uploadMessage}
        </div>
      )}
      <Stack horizontal className={styles.questionInputRow}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.docx,.doc"
          multiple
          style={{ display: 'none' }}
        />
        <div
          className={styles.questionInputUploadButtonContainer}
          role="button"
          tabIndex={0}
          aria-label="Upload Resume button"
          onClick={handleUploadClick}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleUploadClick() : null)}
          title="Upload Resume (PDF or Word)">
          <AttachRegular className={styles.questionInputUploadButton} />
        </div>
        <Checkbox
          className={styles.updateExistingCheckbox}
          label="Update if exists"
          checked={updateExisting}
          onChange={(_, checked) => setUpdateExisting(checked || false)}
          title="If checked, uploading a resume for an existing candidate will update their record"
        />
        <TextField
          className={styles.questionInputTextArea}
          placeholder={placeholder}
          multiline
          resizable={false}
          borderless
          value={question}
          onChange={onQuestionChange}
          onKeyDown={onEnterPress}
        />
        <div
          className={styles.questionInputSendButtonContainer}
          role="button"
          tabIndex={0}
          aria-label="Ask question button"
          onClick={sendQuestion}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
          {sendQuestionDisabled ? (
            <SendRegular className={styles.questionInputSendButtonDisabled} />
          ) : (
            <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
          )}
        </div>
        <div className={styles.questionInputBottomBorder} />
      </Stack>
    </Stack>
  )
}
